import { effect } from '@preact/signals-react';
import { injectable, inject } from 'src/features/ioc';
import { managedSessionLifecycle } from 'src/lib/featureFlags/flags/managedSessionLifecycle';
import type { SessionState as AuthoritativeSessionState } from 'src/services/SessionService';
import type { ReactBindings } from 'src/types/bindings';

const SESSION_RESTORED_KEY = 'oidc_session_restored';

export interface SessionState {
    isExpired: boolean;
    isRestoring: boolean;
    lastRefreshAttempt?: Date;
}

export type SessionStateCallback = (state: SessionState) => void;

/**
 * Compatibility adapter from authoritative SessionService state to the existing
 * session-expiration presentation API.
 *
 * This service centralizes session state management that was previously spread across
 * React hooks. It handles:
 * - Reactive SessionService and rollout-flag observation
 * - Legacy session-expiration view state
 * - Cross-tab localStorage coordination for session restoration
 */
@injectable()
export class SessionMonitorService {
    #log: ReactBindings['Logger'];
    #state: SessionState = {
        isExpired: false,
        isRestoring: false,
    };
    #subscribers: Set<SessionStateCallback> = new Set();
    #disposeSessionEffect: (() => void) | null = null;
    #storageHandler: ((e: StorageEvent) => void) | null = null;
    #storageCleanupTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(
        @inject('SessionService')
        private readonly sessionService: ReactBindings['SessionService'],
        @inject('FeatureFlagService')
        private readonly featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#log = logger.getSubLogger({ name: 'SessionMonitorService' }, { component: 'auth' });
        this.#initialize();
    }

    /**
     * Returns the current session state.
     */
    getState(): SessionState {
        return { ...this.#state };
    }

    /**
     * Subscribe to session state changes.
     * @returns Unsubscribe function
     */
    subscribe(callback: SessionStateCallback): () => void {
        this.#subscribers.add(callback);

        // Immediately notify with current state
        try {
            callback(this.getState());
        } catch (error) {
            this.#log.error('Error in SessionMonitorService subscriber callback during initial notification', {
                error,
            });
        }

        return () => {
            this.#subscribers.delete(callback);
        };
    }

    /**
     * Signal session restoration across tabs via localStorage.
     * Call this when a login completes in any tab.
     */
    signalSessionRestoration(): void {
        if (typeof window !== 'undefined') {
            localStorage.setItem(SESSION_RESTORED_KEY, Date.now().toString());
        }
    }

    /**
     * Clean up resources. Call when the service is no longer needed.
     */
    dispose(): void {
        this.#disposeSessionEffect?.();
        this.#disposeSessionEffect = null;

        if (this.#storageCleanupTimeout !== null) {
            clearTimeout(this.#storageCleanupTimeout);
            this.#storageCleanupTimeout = null;
        }

        if (this.#storageHandler && typeof window !== 'undefined') {
            window.removeEventListener('storage', this.#storageHandler);
            this.#storageHandler = null;
        }

        this.#subscribers.clear();
    }

    #initialize(): void {
        const lifecycleEnabled = this.featureFlagService.getSignal(managedSessionLifecycle);
        let previousPhase: AuthoritativeSessionState['phase'] | undefined;
        this.#disposeSessionEffect = effect(() => {
            const enabled = lifecycleEnabled.value;
            const session = this.sessionService.state.value;
            if (TRACE) {
                this.#log.trace('Observed session state', { enabled, phase: session.phase });
            }

            if (!enabled) {
                previousPhase = session.phase;
                this.#updateState({
                    isExpired: false,
                    isRestoring: false,
                });
                return;
            }

            switch (session.phase) {
                case 'authenticated':
                    this.#updateState({
                        ...(previousPhase === 'refreshing' ? { lastRefreshAttempt: new Date() } : {}),
                        isExpired: false,
                        isRestoring: false,
                    });
                    break;
                case 'expired':
                    this.#updateState({
                        isExpired: session.reason !== 'logout',
                        isRestoring: false,
                    });
                    break;
                case 'unauthenticated':
                    this.#updateState({
                        isExpired: false,
                        isRestoring: false,
                    });
                    break;
                case 'authenticating':
                case 'refreshing':
                    break;
            }
            previousPhase = session.phase;
        });

        // Set up cross-tab session restoration monitoring
        this.#setupStorageListener();

        this.#log.info('Initialized with SessionService');
    }

    #setupStorageListener(): void {
        if (typeof window === 'undefined') {
            return;
        }

        this.#storageHandler = (e: StorageEvent) => {
            if (e.key === SESSION_RESTORED_KEY && e.newValue) {
                this.#log.info('Cross-tab session restoration signal detected');

                // Another tab completed login, indicate we're restoring
                if (this.#state.isExpired) {
                    this.#updateState({
                        ...this.#state,
                        isRestoring: true,
                    });
                }

                // Clear the storage event flag after a short delay
                if (this.#storageCleanupTimeout !== null) {
                    clearTimeout(this.#storageCleanupTimeout);
                }
                this.#storageCleanupTimeout = setTimeout(() => {
                    this.#storageCleanupTimeout = null;
                    localStorage.removeItem(SESSION_RESTORED_KEY);
                }, 100);
            }
        };

        window.addEventListener('storage', this.#storageHandler);
    }

    #updateState(newState: SessionState): void {
        if (
            this.#state.isExpired === newState.isExpired &&
            this.#state.isRestoring === newState.isRestoring &&
            this.#state.lastRefreshAttempt?.getTime() === newState.lastRefreshAttempt?.getTime()
        ) {
            return;
        }

        this.#state = newState;

        // Notify all subscribers
        const state = this.getState();
        for (const callback of this.#subscribers) {
            try {
                callback(state);
            } catch (error) {
                this.#log.error('Error in SessionMonitorService subscriber callback', { error });
            }
        }
    }
}
